import BigNumber from 'bignumber.js'
import { match, P } from 'ts-pattern'

import { BigNumber as BigNumber_, Duration, Schema } from '@blade/std-lib'
import { Network } from '@root/entities/network/model'
import { TradeType } from '@root/entities/trade-type/model'

import type {
  TradeSettingsByNetworkListData,
  TradeSettingsPresetUpdatePayload,
} from '@blade/trade-api-client'

type PresetDto = TradeSettingsByNetworkListData['presets'][number]
type SettingsDto = TradeSettingsPresetUpdatePayload['settings']

const CommonSchema = Schema.Struct({
  deadline: Schema.DurationFromMillis,
  slippage: Schema.Number,
})

export type SettingsType = TradeType.Type | 'quick-buy'

export const SvmSchema = Schema.Struct({
  ...CommonSchema.fields,
  network: Schema.Literal(Network.Solana),
  jitoEnabled: Schema.Boolean,
  jitoTip: Schema.Number,
  priorityFee: Schema.Number,
})

export interface SvmSettings extends Schema.Schema.Type<typeof SvmSchema> {}

export const EvmSchema = Schema.Struct({
  ...CommonSchema.fields,
  network: Schema.Literals([Network.Ethereum, Network.Base, Network.Bsc]),
  antiMevEnabled: Schema.Boolean,
  gasPrice: Schema.UndefinedOrFromOptional(BigNumber_.FromString),
  maxGasPrice: Schema.UndefinedOrFromOptional(BigNumber_.FromString),
  maxPriorityFeePerGas: Schema.UndefinedOrFromOptional(BigNumber_.FromString),
})
export interface EvmSettings extends Schema.Schema.Type<typeof EvmSchema> {}

export const TonSchema = Schema.Struct({
  ...CommonSchema.fields,
  network: Schema.Literal(Network.Ton),
  gasPrice: BigNumber_.FromString,
})

export const SettingsSchema = Schema.Union([SvmSchema, EvmSchema, TonSchema])

export interface TonSettings extends Schema.Schema.Type<typeof TonSchema> {}

export type TradeSettings = SvmSettings | EvmSettings | TonSettings

export const TradeSettingsByNetworkSchema = Schema.Struct({
  [Network.Solana]: SvmSchema,
  [Network.Ethereum]: EvmSchema,
  [Network.Ton]: TonSchema,
  [Network.Base]: EvmSchema,
  [Network.Bsc]: EvmSchema,
})
export interface SettingsByNetwork
  extends Schema.Schema.Type<typeof TradeSettingsByNetworkSchema> {}

export const fromDto = (
  dto: SettingsDto,
  network: Network.Network,
): TradeSettings => {
  return match(network)
    .returnType<TradeSettings>()
    .with(Network.Solana, (): SvmSettings => {
      type SolanaDto = Extract<PresetDto, { jitoEnabled: boolean }>
      const dtoAsserted = dto as SolanaDto
      const settings: SvmSettings = {
        network: Network.Solana,
        jitoEnabled: dtoAsserted.jitoEnabled,
        jitoTip: dtoAsserted.jitoTip,
        priorityFee: dtoAsserted.priorityFee,
        deadline: Duration.millis(dtoAsserted.deadlineMs),
        slippage: dtoAsserted.slippage,
      }

      return settings
    })
    .with(
      P.union(Network.Ethereum, Network.Base, Network.Bsc),
      (): EvmSettings => {
        type EvmDto = Extract<PresetDto, { maxGasPrice?: number }>
        const dtoAsserted = dto as EvmDto

        const settings: EvmSettings = {
          network: Network.Ethereum,
          deadline: Duration.millis(dtoAsserted.deadlineMs),
          slippage: dtoAsserted.slippage,
          // TODO: Check with BE
          antiMevEnabled: true,
          gasPrice: undefined,
          maxGasPrice: dtoAsserted.maxGasPrice
            ? BigNumber(dtoAsserted.maxGasPrice)
            : undefined,
          maxPriorityFeePerGas: dtoAsserted.maxPriorityFeePerGas
            ? BigNumber(dtoAsserted.maxPriorityFeePerGas)
            : undefined,
        }

        return settings
      },
    )
    .with(Network.Ton, (): TonSettings => {
      const settings: TonSettings = {
        network: Network.Ton,
        deadline: Duration.millis(dto.deadlineMs),
        slippage: dto.slippage,
        gasPrice: BigNumber(0),
      }
      return settings
    })
    .exhaustive()
}

type UpdateDto = TradeSettingsPresetUpdatePayload
type SettingsUpdateDto = UpdateDto['settings']
export const toDto = (settings: TradeSettings) => {
  return match(settings)
    .returnType<SettingsUpdateDto>()
    .with(
      { network: P.union(Network.Ethereum, Network.Base, Network.Bsc) },
      (settings): SettingsUpdateDto => ({
        maxGasPrice: settings.maxGasPrice?.toNumber(),
        maxPriorityFeePerGas: settings.maxPriorityFeePerGas?.toNumber(),
        deadlineMs: Duration.toMillis(settings.deadline),
        slippage: settings.slippage,
        gasPrice: settings.gasPrice?.toNumber(),
      }),
    )
    .with({ network: Network.Ton }, (settings) => ({
      gasPrice: settings.gasPrice.toNumber(),
      deadlineMs: Duration.toMillis(settings.deadline),
      slippage: settings.slippage,
    }))
    .with({ network: Network.Solana }, (settings) => ({
      jitoEnabled: settings.jitoEnabled,
      jitoTip: settings.jitoTip,
      deadlineMs: Duration.toMillis(settings.deadline),
      priorityFee: settings.priorityFee,
      slippage: settings.slippage,
    }))
    .exhaustive()
}

export const SVM_NULL_SETTINGS: SvmSettings = {
  deadline: Duration.hours(5),
  slippage: 5,
  jitoEnabled: true,
  jitoTip: 0.0002,
  priorityFee: 0.0001,
  network: Network.Solana,
}
export const EVM_NULL_SETTINGS: EvmSettings = {
  deadline: Duration.hours(5),
  slippage: 5,
  gasPrice: BigNumber(0),
  antiMevEnabled: true,
  network: Network.Ethereum,
  maxGasPrice: BigNumber(0),
  maxPriorityFeePerGas: BigNumber(0),
}

export const TON_NULL_SETTINGS: TonSettings = {
  deadline: Duration.hours(5),
  slippage: 5,
  gasPrice: BigNumber(0),
  network: Network.Ton,
}

export const QUICK_BUY_AMOUNT_NATIVE = {
  [Network.Ethereum]: {
    min: BigNumber(0.001),
    max: BigNumber(1),
  },
  [Network.Solana]: {
    min: BigNumber(0.001),
    max: BigNumber(10),
  },
  [Network.Ton]: {
    min: BigNumber(0.001),
    max: BigNumber(10),
  },
  [Network.Base]: {
    min: BigNumber(0.001),
    max: BigNumber(1000),
  },
  [Network.Bsc]: {
    min: BigNumber(0.001),
    max: BigNumber(1000),
  },
}

export const nullSettings = (network: Network.Network) =>
  match(network)
    .with(Network.Solana, () => SVM_NULL_SETTINGS)
    .with(
      P.union(Network.Ethereum, Network.Base, Network.Bsc),
      () => EVM_NULL_SETTINGS,
    )
    .with(Network.Ton, () => TON_NULL_SETTINGS)
    .exhaustive()

export const isAntiMevEnabled = (settings: TradeSettings) => {
  if (settings.network === Network.Ton) {
    return undefined
  }

  if (settings.network === Network.Solana) {
    return settings.jitoEnabled
  }

  return settings.antiMevEnabled
}

export const isLimitType = (type: SettingsType) => {
  return type !== 'quick-buy' && TradeType.isLimit(type)
}

export const equivalence = Schema.toEquivalence(SettingsSchema)
